// generic-input.component.ts
import { KeyValue } from '@angular/common';
import {
  AfterContentInit,
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  computed,
  effect,
  forwardRef,
  input,
  inject,
} from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { WidgetConfigAccessor } from '../services/widget-config.accessor';
import { AutoDestroy } from '@finxone-platform/shared/base-types';
import {
  InputLabelOptions,
  TypographyKeys,
  baseCssConfigStyle,
  FormFieldConfig,
} from '@finxone-platform/shared/sys-config-types';
import { Subject, takeUntil } from 'rxjs';
import { InputValidationFactory } from '../input-field-validation/input-validations-factory';
import { ValidationConfig } from '../input-field-validation/validation-config.type';

/**
 * A robust input component which provides enhanced form control capabilities with built-in validation and styling flexibility.
 * Find the usage guide here: https://dev.azure.com/finxone/Finxone%20Platform/_wiki/wikis/Finxone-Platform.wiki/353/Finxone-Input-Component-Usage-guide
 */

@Component({
  standalone: false,
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'finxone-input',
  templateUrl: './finxone-input.component.html',
  styleUrls: ['./finxone-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FinxoneInputComponent),
      multi: true,
    },
  ],
})
export class FinxoneInputComponent
  extends InputValidationFactory
  implements ControlValueAccessor, OnInit, OnChanges, AfterContentInit, AfterViewInit
{
  private readonly cd = inject(ChangeDetectorRef);
  private readonly configService = inject(WidgetConfigAccessor);

  @ViewChild('inputElementWrapper') inputElementWrapper: ElementRef;
  @AutoDestroy destroy$: Subject<void> = new Subject<void>();
  @ContentChild('prefix', { static: false }) prefixContent: ElementRef;
  @ContentChild('suffix', { static: false }) suffixContent: ElementRef;
  @ViewChild('myInput') myInputRef!: ElementRef<HTMLInputElement>;
  @Input() inputFieldConfig: FormFieldConfig;
  @Input() public type = 'text';
  @Input() public placeholder = '';
  @Input() public label = '';
  @Input() public inputMode = 'text';
  @Input() public class = '';
  @Input() public readOnly = false;
  @Input() public isDisabled = false;
  @Input() public formControlInvalid = false;
  @Input() public globalCssVariables: Record<string, string> = {};
  @Input() public customInputTemplate?: TemplateRef<unknown>;
  @Input() public customErrorTemplate?: TemplateRef<unknown>;
  @Input() public id = '';
  @Input() public name = '';
  @Input() public value = '';
  @Input() public maxLength: number;
  @Input() public infoText = '';
  @Input() public inputLabelTooltipText = '';
  @Input() public mask: string | null;

  // button inputs
  @Input() public isVerifyButtonShown = false;
  @Input() public isVerifyButtonGlobalStylingAllowed = false;
  @Input() public verifyButtonGlobalStylingClass = '';
  @Input() public verifyButtonStyle: baseCssConfigStyle;
  @Input() public verifyButtonTextContent = '';
  @Input() public buttonType = 'button';
  @Input() public disableAutocomplete = false;

  // Validation Configuration
  @Input()
  validationConfig: ValidationConfig[] = [];
  @Input() showErrors = true;

  @Output() public inputChanged = new EventEmitter();
  @Output() public blurEvent = new EventEmitter();
  @Output() buttonClicked = new EventEmitter<void>();

  // Internal form control
  public control = new FormControl();

  // Parent form control
  @Input() parentControl?: FormControl;

  private isWorkflow = false;

  // Used for ControlValueAccessor
  // eslint-disable-next-line @typescript-eslint/ban-types
  onChange: Function = () => {};
  // eslint-disable-next-line @typescript-eslint/ban-types
  onTouched: Function = () => {};

  public labelType: InputLabelOptions;
  public labelTypeOptions = InputLabelOptions;
  public textStylingClass: TypographyKeys;
  public borderRadiusClass = '';
  @Input() public isInputOnFocus = false;
  @Output() public isInputOnFocusChange = new EventEmitter<boolean>();

  private cssVars: Record<string, string> = {};
  public hasPrefixContent = false;
  public hasSuffixContent = false;
  public inputHeight: number;

  public spaceBetweenElementUnit = input('');
  public spaceBetweenElementValue = input(0);

  protected readonly spaceBetweenItem = computed(() => {
    return `${this.spaceBetweenElementValue()}${this.spaceBetweenElementUnit()}`;
  });
  public tooltipBorderRadiusClass = '';
  public tooltipTextStyleClass = '';

  constructor() {
    super();
    effect(() => {
      const globalCssVariables = this.configService.globalCssVariables();
      if (globalCssVariables) {
        this.cssVars = globalCssVariables;
        //get the label type and text style class from root styles
        this.labelType = this.getCssVariableValue('--inputFieldLabelType') as InputLabelOptions;
        this.textStylingClass = this.getCssVariableValue('--inputFieldTextStylingClass') as TypographyKeys;
        this.borderRadiusClass = this.getCssVariableValue('--inputFieldBorderRadiusClass');
        this.tooltipTextStyleClass = this.getCssVariableValue('--tooltipTextStyleClass');
        this.tooltipBorderRadiusClass = this.getCssVariableValue('--tooltipBorderRadiusClass');
      }
    });
  }

  ngOnInit() {
    if (this.parentControl) this.control = this.parentControl;
    this.control.statusChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.cd.markForCheck();
    });
  }

  ngAfterContentInit() {
    // Check if the prefix content exists
    if (this.prefixContent) {
      this.hasPrefixContent = true;
    }
    // Check if the suffix content exists
    if (this.suffixContent) {
      this.hasSuffixContent = true;
    }
  }

  ngAfterViewInit(): void {
    // Use requestAnimationFrame to manage DOM updates efficiently
    requestAnimationFrame(() => {
      if (this.myInputRef?.nativeElement) {
        this.inputHeight = this.myInputRef.nativeElement.offsetHeight;
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['value']?.currentValue !== undefined) {
      this.control.setValue(changes['value']?.currentValue);
    }
    if (changes['validationConfig']?.currentValue !== undefined) {
      const validators = this.setupValidation(this.validationConfig);
      this.control.setValidators(validators);
      this.control.updateValueAndValidity();
    }
    if (!this.parentControl) {
      if (changes['formControlInvalid']?.currentValue !== undefined) {
        this.setFormControlValidity(this.formControlInvalid);
      }
    }
  }

  private setFormControlValidity(formControlInvalid: boolean) {
    if (!formControlInvalid) {
      this.control.markAsPristine();
      this.control.markAsUntouched();
    } else {
      this.control.markAsDirty();
      this.control.markAsTouched();
    }
  }

  public writeValue(value: string): void {
    this.value = value;
  }

  public registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    isDisabled ? this.control.disable() : this.control.enable();
  }

  public onInputChange(event: Event) {
    const target = event.target as HTMLInputElement;
    let value = target.type === 'checkbox' ? target.checked : target.value;

    // Apply mask if provided and input type is text
    if (typeof this.mask === 'string' && this.mask && this.type === 'text') {
      value = this.applyMask(value as string, this.mask as string);
      target.value = value; // update input value for user
    }

    this.onChange(value);
    this.inputChanged.emit();
    this.cd.markForCheck();
  }

  // Simple mask application for sort code (e.g., '##-##-##')
  private applyMask(value: string, mask: string): string {
    // Remove all non-digit characters
    const digits = value.replace(/\D/g, '');
    let masked = '';
    let digitIndex = 0;
    for (let i = 0; i < mask.length && digitIndex < digits.length; i++) {
      if (mask[i] === '#') {
        masked += digits[digitIndex++];
      } else {
        masked += mask[i];
      }
    }
    return masked;
  }

  private getCssVariableValue(variableName: string): string {
    return this.cssVars[variableName];
  }

  public handleLabelClick(): void {
    if (this.labelType === this.labelTypeOptions.FLOATING) {
      this.inputElementWrapper?.nativeElement.querySelector('input')?.focus();
    }
  }

  public onClickButton() {
    this.buttonClicked.emit();
  }

  public getErrorMessageBasedOnKey(error: KeyValue<string, any>): string {
    const key = error.key;
    if (!this.validationConfig.some((config) => config.type === key) && typeof error.value === 'string') {
      return error.value;
    } else {
      const matchingConfig = this.validationConfig.find((config) => config.type === key);
      if (matchingConfig) {
        return this.getErrorMessage(matchingConfig);
      }
    }
    return 'Cannot find error message';
  }

  public setFocusState(state: boolean): void {
    this.isInputOnFocus = state;
    this.isInputOnFocusChange.emit(state);
  }
}
